import { useCallback, useEffect, useRef, useState } from 'react';

import type {
    ChatData,
    ChatMessageData,
    ChatMessagesResponse,
} from '@/types/chat';

interface UseChatOptions {
    orderUuid: string;
    currentUserId?: number;
    onAuthFailure?: () => void;
}

interface UseChatReturn {
    chat: ChatData | null;
    messages: ChatMessageData[];
    isLoading: boolean;
    isSending: boolean;
    isTyping: boolean;
    isResolved: boolean;
    loadChat: () => Promise<void>;
    sendMessage: (body: string | null, images?: File[]) => Promise<void>;
    createChat: (category: string) => Promise<void>;
    resolveChat: () => Promise<void>;
    markAsRead: () => Promise<void>;
    setTyping: (typing: boolean) => void;
}

let notificationSound: HTMLAudioElement | null = null;

function playNotificationSound(): void {
    if (typeof Audio === 'undefined') {
        return;
    }

    if (!notificationSound) {
        notificationSound = new Audio('/sounds/chat-notification.mp3');
    }

    notificationSound.currentTime = 0;
    notificationSound.play().catch(() => {
        // Browser blocked autoplay — ignore
    });
}

// Module-level dedup — shared across all hook instances and StrictMode re-renders
const globalSeenIds = new Set<number>();
let globalSubscribedChannel: string | null = null;

export function useChat({
    orderUuid,
    currentUserId,
    onAuthFailure,
}: UseChatOptions): UseChatReturn {
    const [chat, setChat] = useState<ChatData | null>(null);
    const [messages, setMessages] = useState<ChatMessageData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isResolved, setIsResolved] = useState(false);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onAuthFailureRef = useRef(onAuthFailure);
    onAuthFailureRef.current = onAuthFailure;

    const getXsrfToken = (): string => {
        const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
        return match ? decodeURIComponent(match[1]) : '';
    };

    const loadChat = useCallback(async () => {
        if (!orderUuid) return;
        setIsLoading(true);
        try {
            const response = await fetch(`/orders/${orderUuid}/chat`, {
                headers: { Accept: 'application/json' },
            });

            if (response.status === 401 || response.status === 403) {
                onAuthFailureRef.current?.();
                return;
            }

            const data: ChatMessagesResponse = await response.json();

            if (data.chat) {
                setChat(data.chat);
                const msgs = data.messages?.data.reverse() ?? [];
                msgs.forEach((m) => globalSeenIds.add(m.id));
                setMessages(msgs);
                setIsResolved(data.chat.status === 'resolved');
            } else {
                setChat(null);
                setMessages([]);
            }
        } finally {
            setIsLoading(false);
        }
    }, [orderUuid]);

    const createChat = useCallback(
        async (category: string) => {
            if (!orderUuid) return;
            const response = await fetch(`/orders/${orderUuid}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-XSRF-TOKEN': getXsrfToken(),
                },
                body: JSON.stringify({ category }),
            });

            const data = await response.json();
            if (data.chat) {
                setChat(data.chat);
                const msgs: ChatMessageData[] = data.messages ?? [];
                msgs.forEach((m) => globalSeenIds.add(m.id));
                setMessages(msgs);
                setIsResolved(false);
            }
        },
        [orderUuid],
    );

    const sendMessage = useCallback(
        async (body: string | null, images?: File[]) => {
            if (!chat) return;
            setIsSending(true);

            try {
                const formData = new FormData();
                if (body) formData.append('body', body);
                if (images) {
                    images.forEach((file) => formData.append('images[]', file));
                }

                await fetch(`/chats/${chat.uuid}/messages`, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-XSRF-TOKEN': getXsrfToken(),
                    },
                    body: formData,
                });
                // Message will appear via the broadcast event
            } finally {
                setIsSending(false);
            }
        },
        [chat],
    );

    const resolveChat = useCallback(async () => {
        if (!chat) return;

        await fetch(`/chats/${chat.uuid}/resolve`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-XSRF-TOKEN': getXsrfToken(),
            },
        });

        setIsResolved(true);
        setChat((prev) => (prev ? { ...prev, status: 'resolved' } : null));
    }, [chat]);

    const markAsRead = useCallback(async () => {
        if (!chat) return;

        await fetch(`/chats/${chat.uuid}/messages/read`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-XSRF-TOKEN': getXsrfToken(),
            },
        });
    }, [chat]);

    const setTypingState = useCallback(
        (typing: boolean) => {
            if (!chat || !window.Echo) return;

            if (typing) {
                const channel = window.Echo.join(`chat.${chat.uuid}`);
                channel.whisper('typing', { typing: true });
            }
        },
        [chat],
    );

    // Subscribe to real-time events
    useEffect(() => {
        if (!chat || !window.Echo) return;

        const chatUuid = chat.uuid;

        // Module-level guard — only one subscription globally
        if (globalSubscribedChannel === chatUuid) return;

        // Leave previous channel if switching chats
        if (globalSubscribedChannel) {
            window.Echo.leave(`chat.${globalSubscribedChannel}`);
            window.Echo.leave(`presence-chat.${globalSubscribedChannel}`);
        }

        globalSubscribedChannel = chatUuid;

        window.Echo.private(`chat.${chatUuid}`)
            .listen('.ChatMessageSent', (data: ChatMessageData) => {
                // Dedup: the messages array updater below also checks by ID,
                // so even if this listener fires more than once the message
                // only appears once in the array (and the derived unread
                // count stays correct).
                setMessages((prev) => {
                    if (prev.some((m) => m.id === data.id)) return prev;
                    return [...prev, data];
                });
                setIsTyping(false);
                if (!currentUserId || data.user.id !== currentUserId) {
                    playNotificationSound();
                }
            })
            .listen('.ChatMessageRead', (data: { user_id: number }) => {
                // Only mark messages that the reader actually read
                // (messages NOT sent by the reader)
                setMessages((prev) =>
                    prev.map((m) =>
                        m.user.id !== data.user_id
                            ? { ...m, is_read: true }
                            : m,
                    ),
                );
            })
            .listen('.ChatResolved', () => {
                setIsResolved(true);
                setChat((prev) =>
                    prev ? { ...prev, status: 'resolved' } : null,
                );
            });

        // Presence channel for typing
        window.Echo.join(`chat.${chatUuid}`).listenForWhisper('typing', () => {
            setIsTyping(true);
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            typingTimeoutRef.current = setTimeout(() => {
                setIsTyping(false);
            }, 2000);
        });
    }, [chat, currentUserId]);

    return {
        chat,
        messages,
        isLoading,
        isSending,
        isTyping,
        isResolved,
        loadChat,
        sendMessage,
        createChat,
        resolveChat,
        markAsRead,
        setTyping: setTypingState,
    };
}
